import { MenuItem, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  PossibleQuestionModelType,
  Question,
  Sector,
} from '@/domain/impact/impact.entity';
import { selectImpactEnvQuestionsMap } from '@/domain/impact/impact.selectors.ts';
import { EnvTopicValues } from '@/domain/impact-calculate/entities/calculate.entity';
import { submitImpactEnvResponse } from '@/domain/impact-env/actions/submit-impact-env-response.action';
import {
  EnvQuestionValue,
  EnvSection,
  EnvTopic,
} from '@/domain/impact-env/entities/question.entity';
import {
  selectEnvDebugMode,
  selectImpactEnvAnswersMap,
} from '@/domain/impact-env/impact-env.selectors.ts';
import { selectCurrentProject } from '@/domain/project/project.selectors';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { QuizLayout } from '@/ui/pages/app/components/quiz-panel/QuizLayout';
import { getEnvSectionName } from '@/ui/utils/ess-env-names-and-format.ts';

import { QuizPanelHideCompletedCheckBox } from '../QuizPanelHideCompletedCheckBox';
import { prepareEnvTopicsDisplayed } from './prepare-env-topics-displayed';
import { questionsBySectorEnv } from './questions-by-sector-env';

type Inputs = { [key: string]: string };

interface IProps {
  topic?: EnvTopic;
  sector?: Sector;
  envQuizLayout: Record<EnvTopic, { sections: Array<EnvSection> }>;
}

export function EnvQuiz({ topic, sector, envQuizLayout }: IProps) {
  const dispatch = useAppDispatch();

  const [selectedTopic, setSelectedTopic] = useState<EnvTopic>(
    EnvTopicValues[1],
  );

  useEffect(() => {
    if (topic && EnvTopicValues.includes(topic)) {
      setSelectedTopic(topic);
    }
  }, [topic]);

  const project = useAppSelector(selectCurrentProject);
  const questions: Map<
    Question<PossibleQuestionModelType.ENV>['paramId'],
    Question<PossibleQuestionModelType.ENV>
  > = useAppSelector(selectImpactEnvQuestionsMap);
  const responses: Map<string, EnvQuestionValue> = useAppSelector(
    selectImpactEnvAnswersMap,
  );

  const envTopicsDisplayed = prepareEnvTopicsDisplayed(envQuizLayout);

  const onSubmitResponse = (response: EnvQuestionValue) => {
    dispatch(
      submitImpactEnvResponse({ projectId: project?.id || -1, response }),
    );
  };

  return (
    <Stack direction={'column'} justifyContent={'start'}>
      <QuizPanelHideCompletedCheckBox />
      <TopicPicker
        topics={envTopicsDisplayed}
        selectedTopic={selectedTopic}
        setSelectedTopic={setSelectedTopic}
      />
      <EnvForm
        questions={questions}
        responses={responses}
        selectedTopic={selectedTopic}
        sector={sector}
        envQuizLayout={envQuizLayout}
        onSubmitResponse={onSubmitResponse}
      />
    </Stack>
  );
}

const TopicPicker = ({
  topics,
  selectedTopic,
  setSelectedTopic,
}: {
  topics: EnvTopic[];
  selectedTopic: EnvTopic;
  setSelectedTopic: (topic: EnvTopic) => void;
}) => {
  return (
    <Stack sx={{ my: 2 }}>
      <TextField
        select
        onChange={(e) => setSelectedTopic(e.target.value as EnvTopic)}
        value={selectedTopic}
        sx={{ color: 'white' }}
      >
        {topics.map((topic) => (
          <MenuItem key={topic} value={topic}>
            {translate(`Env.Topics.${topic}`)}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

const EnvForm = ({
  questions,
  responses,
  selectedTopic,
  sector,
  envQuizLayout,
  onSubmitResponse,
}: {
  questions: Map<
    Question<PossibleQuestionModelType.ENV>['paramId'],
    Question<PossibleQuestionModelType.ENV>
  >;
  responses: Map<string, EnvQuestionValue>;
  selectedTopic: EnvTopic;
  sector?: Sector;
  envQuizLayout: Record<EnvTopic, { sections: Array<EnvSection> }>;
  onSubmitResponse: (response: EnvQuestionValue) => void;
}) => {
  const { handleSubmit, register, formState, control } = useForm<Inputs>({
    defaultValues: defineValuesFromExistingResponses(responses),
    mode: 'onChange',
  });

  const { errors } = formState;

  const onSubmitInput = (data: Inputs, envKey: string) => {
    const response: EnvQuestionValue = {
      id: -1,
      paramId: envKey,
      value: data[envKey],
    };
    onSubmitResponse(response);
  };

  const debugMode = useAppSelector(selectEnvDebugMode);

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <QuizLayout<PossibleQuestionModelType.ENV>
        questions={questions}
        selectedTopic={selectedTopic}
        register={register}
        errors={errors}
        control={control}
        handleSubmit={handleSubmit}
        sector={sector}
        sections={envQuizLayout[selectedTopic].sections}
        questionsBySector={questionsBySectorEnv}
        sectionNameFunction={getEnvSectionName}
        debugMode={debugMode}
        onSubmitInput={onSubmitInput}
      />
    </form>
  );
};

function defineValuesFromExistingResponses(
  responses: Map<string, EnvQuestionValue>,
) {
  const values: Record<string, string> = {};
  for (const response of responses.values()) {
    values[response.paramId] = response.value;
  }
  return values;
}
